import type { GeometryKey } from '../locales/keys';

export type PythagorasMode = 'squares' | 'converse' | 'triples' | 'shapes';
export type ShapeKind = 'square' | 'semicircle' | 'equilateral';

export interface PythagorasPreset {
  id: string;
  mode: PythagorasMode;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  /** Leg lengths [a, b] for squares/triples/shapes modes */
  legs?: [number, number];
  /** Three side lengths [a, b, c] for converse mode (SSS triangle) */
  sides?: [number, number, number];
}

export const PYTHAGORAS_PRESETS: readonly PythagorasPreset[] = [
  // Foundation presets
  {
    id: '3-4-5',
    mode: 'squares',
    labelKey: 'pythagoras.preset.3-4-5',
    advancedOnly: false,
    legs: [3, 4],
  },
  {
    id: '5-12-13',
    mode: 'squares',
    labelKey: 'pythagoras.preset.5-12-13',
    advancedOnly: false,
    legs: [5, 12],
  },
  {
    id: '1-1-sqrt2',
    mode: 'squares',
    labelKey: 'pythagoras.preset.1-1-sqrt2',
    advancedOnly: false,
    legs: [1, 1],
  },
  // Advanced presets
  {
    id: '8-15-17',
    mode: 'squares',
    labelKey: 'pythagoras.preset.8-15-17',
    advancedOnly: true,
    legs: [8, 15],
  },
  {
    id: 'almost-right-4-5-6',
    mode: 'converse',
    labelKey: 'pythagoras.preset.almost-right-4-5-6',
    advancedOnly: true,
    sides: [4, 5, 6],
  },
];

export function isValidPythagorasPreset(p: PythagorasPreset): boolean {
  if (typeof p.id !== 'string' || p.id.length === 0) return false;
  const validModes: PythagorasMode[] = ['squares', 'converse', 'triples', 'shapes'];
  if (!validModes.includes(p.mode)) return false;
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) return false;
  if (typeof p.advancedOnly !== 'boolean') return false;
  if (p.legs !== undefined) {
    if (!Array.isArray(p.legs) || p.legs.length !== 2) return false;
    for (const s of p.legs) {
      if (typeof s !== 'number' || s <= 0) return false;
    }
  }
  if (p.sides !== undefined) {
    if (!Array.isArray(p.sides) || p.sides.length !== 3) return false;
    for (const s of p.sides) {
      if (typeof s !== 'number' || s <= 0) return false;
    }
  }
  return true;
}

export function validatePythagorasPresetIds(presets: readonly PythagorasPreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) return false;
    ids.add(preset.id);
  }
  return true;
}
